// ─────────────────────────────────────────────────────────────────────────────
// Mission pipeline — turns a waypoint list into the sequence of phases an
// operator actually thinks in ("Lowering parcel", "Returning home"), and says
// which one the drone is on right now.
//
// "running · 43%" tells you a mission is alive but not what it is DOING. On a
// winch delivery the difference between "descending", "lowering the parcel" and
// "climbing out" is the whole flight.
// ─────────────────────────────────────────────────────────────────────────────

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Human phase for one waypoint. `cruiseAlt` is the mission's highest waypoint
 * altitude, which is what separates a transit leg from a descent to the drop.
 */
export function stageLabel(wp, cruiseAlt) {
  const type = String(wp?.type || '').toUpperCase();

  switch (type) {
    case 'TAKEOFF': return 'Taking off';
    case 'CHANGE_SPEED': return 'Setting speed';
    case 'LOITER_TIME':
    case 'LOITER_TURNS':
    case 'LOITER_UNLIM':
    case 'DELAY': return 'Holding position';
    case 'DO_WINCH': return num(wp.param3) >= 0 ? 'Lowering parcel' : 'Retracting winch';
    case 'DO_SET_SERVO': return 'Releasing parcel';
    case 'DO_GRIPPER': return 'Releasing parcel';
    case 'RTL': return 'Returning home';
    case 'LAND': return 'Landing';
    case 'ROI':
    case 'DO_SET_ROI': return 'Aiming camera';
    case 'CAMERA':
    case 'DO_DIGICAM_CONTROL': return 'Capturing';
    case 'WAYPOINT': {
      const alt = num(wp.altitude !== undefined ? wp.altitude : wp.alt);
      if (cruiseAlt > 0 && alt > 0 && alt < cruiseAlt - 1) return 'Descending to drop';
      return 'In transit';
    }
    default: return type ? type.replace(/_/g, ' ').toLowerCase() : 'In transit';
  }
}

/**
 * The whole pipeline, one entry per waypoint, with consecutive duplicate labels
 * collapsed — "In transit, In transit, In transit" is one phase to a human, and
 * `waypointIndexes` keeps the mapping back to the real sequence.
 */
export function missionStages(waypoints) {
  const wps = Array.isArray(waypoints) ? waypoints : [];
  const cruiseAlt = wps.reduce((max, wp) => Math.max(max, num(wp.altitude !== undefined ? wp.altitude : wp.alt)), 0);

  const stages = [];
  wps.forEach((wp, i) => {
    const label = stageLabel(wp, cruiseAlt);
    const last = stages[stages.length - 1];
    if (last && last.label === label) last.waypointIndexes.push(i);
    else stages.push({ label, waypointIndexes: [i] });
  });
  return stages;
}

/**
 * Which phase the drone is on.
 *
 * `currentWaypoint` is what the backend reports: ArduPilot's 1-based sequence
 * over the uploaded items (HOME excluded), so item N is `waypoints[N - 1]`. 0
 * means the mission is uploaded but no item has been reached yet.
 *
 * Returns null when there is nothing meaningful to show.
 */
export function currentStage(waypoints, currentWaypoint) {
  const stages = missionStages(waypoints);
  if (stages.length === 0) return null;

  const wpIndex = Math.max(0, Math.min(num(currentWaypoint) - 1, (waypoints || []).length - 1));
  const stageIndex = stages.findIndex((s) => s.waypointIndexes.includes(wpIndex));
  const idx = stageIndex >= 0 ? stageIndex : 0;

  return {
    label: stages[idx].label,
    index: idx,          // 0-based position in the pipeline
    total: stages.length,
    stages: stages.map((s) => s.label),
  };
}
